import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import FollowUpCoordinatorDirectoryTable from '@/tables/FollowUpCoordinatorDirectoryTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-coordinators/')({
  component: FollowUpCoordinatorsScreen,
});

/**
 * `themes/wbs/buddypress/groups/single/coordinators/all-coordinators/
 * all-coordinators.php` — the *All Coordinators* tab of a follow-up partner's
 * own group.
 *
 * TOP-LEVEL, NOT A CHILD OF `/follow-ups`. Legacy registers Coordinators as its
 * own group extension (`follow-up-group-tabs/index.php` registers
 * `followUpCoordinatorsTab`, whose `groupTypes` is `follow-up-partner-group`,
 * `follow-up-coordinators-tab.php:9-10`), a SIBLING of the follow-up tab set;
 * and `/follow-ups` is a shell that paints a hardcoded
 * `{pending-requests, entries}` strip over whatever is mounted beneath it
 * (`follow-ups.tsx:36-39`, `:58`) — for a follow-up partner, exactly the caller
 * this route admits — so a child there would render a two-tab bar with neither
 * tab active. D24 re-homes group tabs to top-level routes — the same move
 * `follow-up-network.tsx` made.
 *
 * NO Add BUTTON, unlike the partner directory: `all-coordinators.php` carries no
 * `class="button"` element and the live page has no *Add Coordinator* anywhere.
 * A partner cannot mint a coordinator; the relation is written by the accept.
 *
 * ONE TITLE, where legacy renders two (an `h3` of the tab label plus a
 * datatable caption of the group name, `base-table.php:4-10`). The caption is
 * dropped with the group (D24), and 31 coordinator groups have 31 distinct
 * owners, so nothing is ambiguated by its absence. A bare `Title`, not
 * `TitleWithQuestion`: the contextual-help store is not ported and `HelpModal`
 * falls back silently to its FIRST item on an id miss
 * (`common/HelpModal.tsx:42-47`), i.e. it would render someone else's help text.
 */
function FollowUpCoordinatorsScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();

  // SINGLE-ROLE, and narrower than the follow-up-network shell one route over.
  // `CurrentFollowupPartnerDependency` refuses everyone else — administrators
  // included, because `admin_bypass` is OFF on this surface: both tab classes
  // set `$this->parentCheck = false` and override no `$adminCanView`, so
  // `matchRequirements` takes neither admin door and the live tab 404s for the
  // site admin. Copying `follow-up-network.tsx:38-49`'s three-arm gate here
  // would render a working page over a 403.
  //
  // `me.profile_type`, never `useCurrentUser().isStudyHelper` (which folds
  // coordinator, partner, study helper and admin together,
  // `hooks/useCurrentUser.ts:14-19`) and never `useIsCoordinator` (which reads
  // `params.groupName` and never resolves on a top-level route,
  // `hooks/useIsCoordinator.ts:33-47`).
  //
  // Defence in depth over the same predicate the endpoint enforces, never the
  // only gate. `isPending` first, or the partner bounces themselves on first
  // paint while `/users/me` is still in flight.
  const canView = me?.profile_type === 'FOLLOWUP_PARTNER';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        <Title>{t('wbs.all-coordinators')}</Title>
      </GroupHeaderWrapper>
      <FollowUpCoordinatorDirectoryTable />
    </PageContainer>
  );
}
